import { flg } from "@aix/webutils/src/ui/logging"
import { reportErrorToSentry } from "@aix/webutils/src/sentry"
import type { ISession } from "@aix/vidmax-shared/src/constants/prismaTypings"
import Cookies from "js-cookie"
import { useCallback, useEffect, useMemo, useState } from "react"
import { getAPIBaseUrl } from "~/lib/env"
import { TURNSTILE_TOKEN_FIELD } from "~/lib/turnstile"
import { api } from "~/utils/api"
import { resetPostHogIdentity } from "~/utils/posthogClient"

const baseUrl = getAPIBaseUrl()

// Track auth failures to prevent infinite redirects
const AUTH_FAILURE_KEY = "auth-failure-count"
const MAX_AUTH_FAILURES = 3
const FAILURE_RESET_MS = 60000 // Reset failure count after 1 minute

const getAuthFailureCount = (): number => {
  try {
    const data = sessionStorage.getItem(AUTH_FAILURE_KEY)
    if (!data) return 0
    const { count, timestamp } = JSON.parse(data)
    // Reset if older than FAILURE_RESET_MS
    if (Date.now() - timestamp > FAILURE_RESET_MS) {
      sessionStorage.removeItem(AUTH_FAILURE_KEY)
      return 0
    }
    return count
  } catch {
    return 0
  }
}

const incrementAuthFailureCount = (): number => {
  const count = getAuthFailureCount() + 1
  sessionStorage.setItem(AUTH_FAILURE_KEY, JSON.stringify({ count, timestamp: Date.now() }))
  return count
}

const resetAuthFailureCount = () => {
  sessionStorage.removeItem(AUTH_FAILURE_KEY)
}

// Helper function to get CSRF token from Auth.js
const getCsrfToken = async (): Promise<string> => {
  try {
    // Use API subdomain in production, localhost in dev
    const response = await fetch(`${baseUrl}/api/auth/csrf`, {
      credentials: "include",
    })
    const data = (await response.json()) as { csrfToken?: string }
    return data.csrfToken || ""
  } catch (error) {
    flg.error("Failed to get CSRF token:")
    return ""
  }
}

/**
 * Body fields for a sign-in initiation POST. The Turnstile token is validated
 * server-side before Auth.js runs (VM-954) — omitting it fails the request on
 * production hosts, so every login surface must pass one through.
 */
export const buildSignInFields = ({
  callbackUrl,
  csrfToken,
  email,
  turnstileToken,
}: {
  callbackUrl: string
  csrfToken: string
  email?: string
  turnstileToken?: string | null
}): Record<string, string> => {
  const fields: Record<string, string> = { callbackUrl, csrfToken }
  if (email) fields.email = email
  if (turnstileToken) fields[TURNSTILE_TOKEN_FIELD] = turnstileToken
  return fields
}

type SessionStatus = "loading" | "authenticated" | "unauthenticated"

interface IAuthErrorLike {
  data?: { httpStatus?: number } | null
}

export const isAuthenticationError = (error: IAuthErrorLike | null | undefined): boolean =>
  error?.data?.httpStatus === 401 || error?.data?.httpStatus === 403

export const resolveSessionStatus = ({
  isLoading,
  session,
  error,
  hasAuthHint,
}: {
  isLoading: boolean
  session: ISession | null | undefined
  error: IAuthErrorLike | null
  hasAuthHint: boolean
}): SessionStatus => {
  if (isLoading) return "loading"
  if (session) return "authenticated"
  if (error && !isAuthenticationError(error)) return hasAuthHint ? "authenticated" : "loading"
  return "unauthenticated"
}

/**
 * Custom auth hook for Vite app with Auth.js integration
 * Handles session management and provides auth utilities
 */
export const useAuthSession = () => {
  const [status, setStatus] = useState<SessionStatus>("loading")
  const [authBlocked, setAuthBlocked] = useState(() => getAuthFailureCount() >= MAX_AUTH_FAILURES)

  // Get session from TRPC with better error handling
  const {
    data: session,
    isLoading,
    error,
    refetch,
  } = api.auth.getSession.useQuery(undefined, {
    retry: (failureCount, queryError) => !isAuthenticationError(queryError) && failureCount < 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Don't refetch constantly
    refetchInterval: false,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    onSuccess: () => {
      // Reset failure count on successful auth
      resetAuthFailureCount()
      setAuthBlocked(false)
    },
    onError: (err) => {
      flg.error("❌ [useAuthSession] Session query error:")
      // API restarts and network failures do not invalidate an existing browser session.
      if (!isAuthenticationError(err)) return

      // Track actual auth failures to prevent infinite redirect loops.
      const count = incrementAuthFailureCount()
      if (count >= MAX_AUTH_FAILURES) {
        flg.error("🛑 [useAuthSession] Max auth failures ( ) reached, blocking redirects")
        setAuthBlocked(true)
      }
    },
  })

  // Update status based on TRPC query state
  useEffect(() => {
    const hasAuthHint = !!(
      Cookies.get("gc-logged-in") ||
      Cookies.get("authjs.session-token") ||
      Cookies.get("__Secure-authjs.session-token")
    )
    const newStatus = resolveSessionStatus({ isLoading, session, error, hasAuthHint })

    if (status !== newStatus) {
      setStatus(newStatus)
    }
  }, [isLoading, error, session, status])

  // Improved cookie management - only update gc-logged-in when we're sure
  useEffect(() => {
    // Only update cookies after initial load is complete
    if (!isLoading) {
      const authJsToken = Cookies.get("authjs.session-token") || Cookies.get("__Secure-authjs.session-token")
      const gcLoggedIn = Cookies.get("gc-logged-in")

      if (session && status === "authenticated") {
        if (!gcLoggedIn) {
          flg.info("✅ [useAuthSession] Setting gc-logged-in cookie")
          Cookies.set("gc-logged-in", "true", { expires: 30 })
        }
      } else if (status === "unauthenticated" && !session) {
        // Only remove cookie if we're sure there's no session

        // Don't remove cookie if Auth.js token still exists
        if (!authJsToken && gcLoggedIn) {
          flg.info("🗑️ [useAuthSession] Removing gc-logged-in cookie (no auth token)")
          Cookies.remove("gc-logged-in")
        } else if (authJsToken && gcLoggedIn) {
          flg.info("⚠️ [useAuthSession] Auth token exists but session is unauthenticated - keeping cookie")
        }
      }
    }
  }, [status, session, isLoading])

  // Check for Auth.js session tokens in cookies for optimistic UI
  const cachedStatus = useMemo(() => {
    const lastAuth = Cookies.get("gc-logged-in")
    const authJsSessionToken = Cookies.get("authjs.session-token") || Cookies.get("__Secure-authjs.session-token")

    const optimisticStatus = status === "loading" && (lastAuth || authJsSessionToken) ? "authenticated" : status

    return optimisticStatus
  }, [status])

  // Sign out function
  const signOut = useCallback(async (options?: { callbackUrl?: string }) => {
    try {
      // Get CSRF token for Auth.js
      const csrfResponse = await fetch(`${baseUrl}/api/auth/csrf`, {
        credentials: "include",
      })
      const { csrfToken } = await csrfResponse.json()

      // Use Auth.js signout endpoint
      const response = await fetch(`${baseUrl}/api/auth/signout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          csrfToken,
          callbackUrl: options?.callbackUrl || "/auth/signin",
        }),
        credentials: "include",
      })

      if (response.ok) {
        // Clear local state
        resetPostHogIdentity()
        localStorage.removeItem("local-email")
        localStorage.removeItem("dev-auth-token")
        Cookies.remove("gc-logged-in")

        // Redirect to sign in page or provided callback URL
        const callbackUrl = options?.callbackUrl || "/auth/signin"
        window.location.href = callbackUrl
      }
    } catch (error) {
      // Network errors during logout are expected (user offline, navigating away,
      // tab backgrounded mid-request). The fallback below still clears local state
      // and redirects, so the user experience is unaffected. Don't pollute Sentry
      // with these — kills VIDMAX-WEB-CS (20 events / 2d).
      const isNetworkError = error instanceof TypeError && /failed to fetch|network/i.test(error.message)
      if (!isNetworkError) {
        void reportErrorToSentry(error, "useAuthSessionVite.signOut")
      }
      // Even if the API call fails, clear local state and redirect
      resetPostHogIdentity()
      localStorage.removeItem("local-email")
      Cookies.remove("gc-logged-in")
      window.location.href = options?.callbackUrl || "/auth/signin"
    }
  }, [])

  // Sign in function (original clean version)
  const signIn = useCallback(
    async (
      providerId?: string,
      options?: { callbackUrl?: string; redirect?: boolean; email?: string; turnstileToken?: string | null },
    ) => {
      const { callbackUrl = window.location.href, redirect = true, email, turnstileToken } = options || {}

      try {
        // Handle case with no provider ID
        if (!providerId) {
          const signInUrl = `/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`
          if (redirect) {
            window.location.href = signInUrl
          }
          return { ok: true, url: signInUrl }
        }

        // If redirect is false, we need to use fetch to get the response
        // (mainly for testing or programmatic usage)
        if (!redirect) {
          const bodyParams = buildSignInFields({
            callbackUrl,
            csrfToken: await getCsrfToken(),
            email,
            turnstileToken,
          })

          const response = await fetch(`${baseUrl}/api/auth/signin/${providerId}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams(bodyParams),
            credentials: "include",
          })

          if (!response.ok) {
            const errorText = await response.text()
            flg.error("Sign-in error:")
            return { ok: false, error: errorText }
          }

          return { ok: true, url: response.url }
        }

        // Use form submission for ALL providers (OAuth and email)
        // This is the standard way auth libraries handle browser-based auth flows
        const form = document.createElement("form")
        form.method = "POST"
        form.action = `${baseUrl}/api/auth/signin/${providerId}`

        // csrfToken is required by Auth.js; cf-turnstile-response by our server-side guard.
        const fields = buildSignInFields({
          callbackUrl,
          csrfToken: await getCsrfToken(),
          email,
          turnstileToken,
        })
        for (const [name, value] of Object.entries(fields)) {
          const input = document.createElement("input")
          input.type = "hidden"
          input.name = name
          input.value = value
          form.appendChild(input)
        }

        // Submit the form (triggers browser navigation)
        document.body.appendChild(form)
        form.submit()

        // Return for consistency (browser will navigate away)
        return { ok: true, url: `${baseUrl}/api/auth/signin/${providerId}` }
      } catch (error) {
        flg.error("Sign in error:")
        return { ok: false, error }
      }
    },
    [],
  )

  // Separate credentials sign in function for admin authentication
  const signInWithCredentials = useCallback(
    async (email: string, password: string, options?: { callbackUrl?: string }) => {
      const { callbackUrl = window.location.href } = options || {}

      try {
        // Use form submission for credentials auth (same as other providers)
        const form = document.createElement("form")
        form.method = "POST"
        form.action = `${baseUrl}/api/auth/callback/credentials`

        // Add CSRF token (required by Auth.js)
        const csrfToken = await getCsrfToken()
        const csrfInput = document.createElement("input")
        csrfInput.type = "hidden"
        csrfInput.name = "csrfToken"
        csrfInput.value = csrfToken
        form.appendChild(csrfInput)

        // Add email
        const emailInput = document.createElement("input")
        emailInput.type = "hidden"
        emailInput.name = "email"
        emailInput.value = email
        form.appendChild(emailInput)

        // Add password
        const passwordInput = document.createElement("input")
        passwordInput.type = "hidden"
        passwordInput.name = "password"
        passwordInput.value = password
        form.appendChild(passwordInput)

        // Add callback URL
        const callbackInput = document.createElement("input")
        callbackInput.type = "hidden"
        callbackInput.name = "callbackUrl"
        callbackInput.value = callbackUrl
        form.appendChild(callbackInput)

        // Submit the form (triggers browser navigation)
        document.body.appendChild(form)
        form.submit()

        // Refetch session after form submission
        void refetch()

        // Return for consistency (browser will navigate)
        return { ok: true, url: callbackUrl }
      } catch (error) {
        flg.error("Credentials sign in error:")
        return { ok: false, error: error instanceof Error ? error.message : "Authentication error" }
      }
    },
    [refetch],
  )

  return {
    session: session,
    status: cachedStatus,
    rawStatus: status,
    authBlocked,
    isLoading,
    refetch,
    signIn,
    signInWithCredentials,
    signOut,
  }
}
